import {
    gql,
    useQuery
} from "@apollo/client";
import {
  FindAllOfferPointsDeductionsQueryQuery,
  QueryFindAllOfferPointsDeductionsArgs
} from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllOfferPointsDeductionsQuery = gql`
  query FindAllOfferPointsDeductionsQuery(
    $page: Int!
    $pageSize: Int!
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllOfferPointsDeductions(
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        offerId
        offerTitle
        offerStatus
        partnerName
        point
        createdAt
        updatedAt
        adminName
        adminLevel
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

type OfferPointsDeductionListItem = NonNullable<
  NonNullable<
    NonNullable<FindAllOfferPointsDeductionsQueryQuery["findAllOfferPointsDeductions"]>["data"]
  >[number]
>;

export interface WithFindAllOfferPointsDeductions {
  data: OfferPointsDeductionListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreOfferPointsDeductions: boolean;
}

interface Props {
  variables: QueryFindAllOfferPointsDeductionsArgs;
  children: (data: WithFindAllOfferPointsDeductions) => React.ReactElement | null;
}

export const FindAllOfferPointsDeductionsController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllOfferPointsDeductionsQueryQuery,
    QueryFindAllOfferPointsDeductionsArgs
  >(findAllOfferPointsDeductionsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const offerPointsDeductions = (data?.findAllOfferPointsDeductions?.data || [])
    .filter((deduction): deduction is OfferPointsDeductionListItem => Boolean(deduction));
  const page = data?.findAllOfferPointsDeductions?.page || variables.page;
  const pageSize = data?.findAllOfferPointsDeductions?.pageSize || variables.pageSize;
  const total = data?.findAllOfferPointsDeductions?.total || 0;
  const totalPages = data?.findAllOfferPointsDeductions?.totalPages || 0;
  const hasMoreOfferPointsDeductions = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreOfferPointsDeductions) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllOfferPointsDeductions) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllOfferPointsDeductions: {
            ...fetchMoreResult?.findAllOfferPointsDeductions,
            data: [
              ...(prev?.findAllOfferPointsDeductions?.data || []),
              ...(fetchMoreResult?.findAllOfferPointsDeductions?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreOfferPointsDeductions]);

  return children({
    data: offerPointsDeductions,
    loading,
    loadMore,
    hasMoreOfferPointsDeductions,
    page,
    pageSize,
    total,
    totalPages,
  });
};
